import * as React from "react";
import * as _ from "lodash";
import { Select, Form, List, Message } from "semantic-ui-react";

type ModeOptions = "Remove" | "Add";
type BindingType = "parameters" | "faults";

const BINDING_TYPE_OPTIONS = [
  { key: "parameters", text: "Parameters", value: "parameters" },
  { key: "faults", text: "Faults", value: "faults" }
];

export type ModifyEscalatorParameterBindingsProps = {
  mode: ModeOptions;
  domains: Api.Domains;
  parameters: Api.EscalatorParameter[];
  faults?: Api.EscalatorFault[];
  escalatorVersions: Api.EscalatorVersions;
  updateEscalatorBindings: (operation: string, escalatorBulkBindings: Api.EscalatorBulkBindings) => void;
  updateEscalatorFaultBindings?: (operation: string, escalatorFaultBulkBindings: Api.EscalatorFaultBulkBindings) => void;
  permission: boolean;
};

type ModifyEscalatorParameterBindingsState = {
  escalatorVersionId: number;
  domainId: number;
  bindingType: BindingType;
  versionOptions: { key: number; text: string; value: number }[];
};

/** ModifyEscalatorParameterBindings component */
class ModifyEscalatorParameterBindings extends React.Component<
  ModifyEscalatorParameterBindingsProps,
  ModifyEscalatorParameterBindingsState
> {
  state = {
    escalatorVersionId: -1,
    domainId: -1,
    bindingType: "parameters" as BindingType,
    versionOptions: [] as { key: number; text: string; value: number }[]
  };

  updateVersionOptions = (): void => {
    const { escalatorVersions, domains } = this.props;
    const { domainId } = this.state;
    const escalatorVersionList = (domainId > 0)
      ? _.values(escalatorVersions).filter(ev => ev.domain.id === domainId)
      : [];
    if (escalatorVersionList.length > 0) {
      const mappedOptions = escalatorVersionList.map(escalatorVersion => ({
        key: escalatorVersion.id,
        text: `${escalatorVersion.number}.${domains[escalatorVersion.domain.id].enumName} - ${escalatorVersion.versionName}`,
        value: escalatorVersion.id
      }));
      const versionOptions = _.sortBy(mappedOptions, ["text"]).reverse();
      this.setState({
        versionOptions,
        escalatorVersionId: versionOptions[0].value
      });
    } else {
      this.setState({
        versionOptions: [],
        escalatorVersionId: -1
      });
    }
  };

  componentDidMount(): void {
    const { parameters } = this.props;
    if (parameters.length > 0) {
      const domainId = parameters[0].domain.id;
      this.setState(() => ({ domainId }));
    }
    this.updateVersionOptions();
  }

  componentDidUpdate(prevProps: ModifyEscalatorParameterBindingsProps, prevState: ModifyEscalatorParameterBindingsState): void {
    if (prevState.domainId !== this.state.domainId) {
      this.updateVersionOptions();
    }
  }

  render(): JSX.Element {
    const { parameters, faults, domains, mode } = this.props;
    const { domainId, escalatorVersionId, versionOptions, bindingType } = this.state;

    if (bindingType === "faults") {
      return this.renderFaultsView(mode, versionOptions, faults || [], domains, escalatorVersionId, domainId);
    }

    // Creates domain dropdown options for filtering parameters
    const usedDomainIds = _.uniq(parameters.map(parameter => parameter.domain.id));
    const domainOptions = usedDomainIds
      .sort((a, b) => domains[a].enumValue - domains[b].enumValue)
      .map(usedDomainId => ({
        key: usedDomainId,
        text: domains[usedDomainId].enumName + " [" + domains[usedDomainId].enumValue + "]",
        value: usedDomainId
      }));

    // Get parameters which are modified
    let selectedParameters: Api.EscalatorParameter[] = [];
    if (mode === "Add") {
      // Get parameters in the given domain which do not contain the given version binding
      selectedParameters = parameters.filter(
        parameter =>
          parameter.domain.id === domainId &&
          !parameter.escalatorParameterBindings.find(
            binding => binding.escalatorVersion.id === escalatorVersionId
          )
      );
    } else {
      // Get parameters in the given domain which contain the given version binding
      selectedParameters = parameters.filter(
        parameter =>
          parameter.domain.id === domainId &&
          !!parameter.escalatorParameterBindings.find(
            binding => binding.escalatorVersion.id === escalatorVersionId
          )
      );
    }
    const selectedParametersCount = selectedParameters.length;
    const infoText =
      mode === "Add"
        ? "Add selected binding to the following parameters"
        : "Remove selected binding from the following parameters";

    const selectedParametersNames = selectedParameters.map(
      parameter => `${parameter.enumName} (code = ${parameter.code})`
    );

    return (
      <>
        {mode === "Add" ? (
          <Message
            info={true}
            header="Add version bindings to parameters"
            list={[
              "Select the binding which will be added to all parameters in the selected domain",
              "The list shows parameters to which the binding will be added",
              "Start adding by clicking 'Add binding'"
            ]}
          />
        ) : (
          <Message
            info={true}
            header="Remove version bindings from parameters"
            list={[
              "Select the binding which will be removed from all parameters in the selected domain",
              "The list shows parameters from which the binding will be removed",
              "Start removing by clicking 'Remove binding'"
            ]}
          />
        )}
        <Form.Group>
          <Form>
            <div className="uidropdown">
              <Form.Field
                id="escalatorBindingTypeDropdown"
                control={Select}
                label="Binding Type"
                options={BINDING_TYPE_OPTIONS}
                value={bindingType}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ bindingType: value as BindingType }))
                }
                disabled={!this.props.permission}
              />
              <Form.Field
                id="escalatorDomainDropdown"
                control={Select}
                label="Domain"
                options={domainOptions}
                search={true}
                value={domainId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ domainId: value }))
                }
                disabled={!this.props.permission}
              />
              <Form.Field
                id="escalatorVersionsDropdown"
                control={Select}
                label="Version"
                options={versionOptions}
                value={escalatorVersionId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ escalatorVersionId: value }))
                }
                disabled={!this.props.permission}
              />
            </div>
          </Form>
        </Form.Group>
        <Form.Group className="bindingParameters">
          {(domainId > 0 && escalatorVersionId > 0 && selectedParametersCount > 0) ? (
            <>
              <label>{`${infoText} (${selectedParametersCount}):`}</label>
              <div>
                <List items={selectedParametersNames} />
              </div>
            </>
          ) : escalatorVersionId <= 0 ? (
            <>
              <label>No version is available under the selected domain.</label>
            </>
          ) : (
            <>
              <label>Selected binding and domain do not match any parameters.</label>
            </>
          )}
        </Form.Group>
        <>
          {mode === "Add" ? (
            <button
              id="addEscalatorBindingsButton"
              disabled={selectedParametersCount === 0 || !this.props.permission || domainId <= 0 || escalatorVersionId <= 0}
              onClick={() => this.updateBindings(mode, selectedParameters)}
              className="bindingsActionBtn"
            >
              Add bindings
            </button>
          ) : (
            <button
              id="removeEscalatorBindingsButton"
              disabled={selectedParametersCount === 0 || !this.props.permission || domainId <= 0 || escalatorVersionId <= 0}
              onClick={() => this.updateBindings(mode, selectedParameters)}
              className="bindingsActionBtn"
            >
              Remove bindings
            </button>
          )}
        </>
      </>
    );
  }

  private renderFaultsView(
    mode: ModeOptions,
    versionOptions: { key: number; text: string; value: number }[],
    faults: Api.EscalatorFault[],
    domains: Api.Domains,
    escalatorVersionId: number,
    domainId: number
  ): JSX.Element {
    // Creates domain dropdown options for filtering faults
    const usedFaultDomainIds = _.uniq(faults.map(fault => fault.domain.id));
    const faultDomainOptions = usedFaultDomainIds
      .filter(id => domains[id])
      .sort((a, b) => domains[a].enumValue - domains[b].enumValue)
      .map(id => ({
        key: id,
        text: domains[id].enumName + " [" + domains[id].enumValue + "]",
        value: id
      }));

    // Get faults which are modified
    let selectedFaults: Api.EscalatorFault[] = [];
    if (mode === "Add") {
      // Get faults in the given domain which do not contain the given version binding
      selectedFaults = faults.filter(
        fault =>
          fault.domain.id === domainId &&
          !(fault.escalatorFaultBindings || []).find(
            binding => binding.escalatorVersion.id === escalatorVersionId
          )
      );
    } else {
      // Get faults in the given domain which contain the given version binding
      selectedFaults = faults.filter(
        fault =>
          fault.domain.id === domainId &&
          !!(fault.escalatorFaultBindings || []).find(
            binding => binding.escalatorVersion.id === escalatorVersionId
          )
      );
    }
    const selectedFaultsCount = selectedFaults.length;
    const infoText =
      mode === "Add"
        ? "Add selected binding to the following faults"
        : "Remove selected binding from the following faults";

    const selectedFaultsNames = selectedFaults.map(
      fault => `${fault.enumName} (code = ${fault.code})`
    );

    return (
      <>
        {mode === "Add" ? (
          <Message
            info={true}
            header="Add version bindings to faults"
            list={[
              "Select the binding which will be added to all faults in the selected domain",
              "The list shows faults to which the binding will be added",
              "Start adding by clicking 'Add bindings'"
            ]}
          />
        ) : (
          <Message
            info={true}
            header="Remove version bindings from faults"
            list={[
              "Select the binding which will be removed from all faults in the selected domain",
              "The list shows faults from which the binding will be removed",
              "Start removing by clicking 'Remove bindings'"
            ]}
          />
        )}
        <Form.Group>
          <Form>
            <div className="uidropdown">
              <Form.Field
                id="escalatorBindingTypeDropdown"
                control={Select}
                label="Binding Type"
                options={BINDING_TYPE_OPTIONS}
                value={this.state.bindingType}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ bindingType: value as BindingType }))
                }
                disabled={!this.props.permission}
              />
              <Form.Field
                id="escalatorDomainDropdown"
                control={Select}
                label="Domain"
                options={faultDomainOptions}
                search={true}
                value={domainId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) => this.setState(() => ({ domainId: value }))}
                disabled={!this.props.permission}
              />
              <Form.Field
                id="escalatorVersionsDropdown"
                control={Select}
                label="Version"
                options={versionOptions}
                value={escalatorVersionId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ escalatorVersionId: value }))
                }
                disabled={!this.props.permission}
              />
            </div>
          </Form>
        </Form.Group>
        <Form.Group className="bindingParameters">
          {(domainId > 0 && escalatorVersionId > 0 && selectedFaultsCount > 0) ? (
            <>
              <label>{`${infoText} (${selectedFaultsCount}):`}</label>
              <div>
                <List items={selectedFaultsNames} />
              </div>
            </>
          ) : escalatorVersionId <= 0 ? (
            <>
              <label>No version is available under the selected domain.</label>
            </>
          ) : (
            <>
              <label>Selected binding and domain do not match any faults.</label>
            </>
          )}
        </Form.Group>
        <>
          {mode === "Add" ? (
            <button
              id="addEscalatorFaultBindingsButton"
              disabled={selectedFaultsCount === 0 || !this.props.permission || domainId <= 0 || escalatorVersionId <= 0}
              onClick={() => this.updateFaultBindings(mode, selectedFaults)}
              className="bindingsActionBtn"
            >
              Add bindings
            </button>
          ) : (
            <button
              id="removeEscalatorFaultBindingsButton"
              disabled={selectedFaultsCount === 0 || !this.props.permission || domainId <= 0 || escalatorVersionId <= 0}
              onClick={() => this.updateFaultBindings(mode, selectedFaults)}
              className="bindingsActionBtn"
            >
              Remove bindings
            </button>
          )}
        </>
      </>
    );
  }

  // Add and Remove binding to parameters
  private updateBindings = (operation: string, parameters: Api.EscalatorParameter[]): void => {
    const { updateEscalatorBindings } = this.props;
    const parameterIdList = parameters.map(parameter => parameter.id) as number[];
    const bindingsDetails: Api.EscalatorBulkBindings = {
      targetVersionId: this.state.escalatorVersionId,
      parameterIdList,
      domainId: this.state.domainId
    };
    updateEscalatorBindings(operation, bindingsDetails);
  };

  // Add and Remove binding to faults
  private updateFaultBindings = (operation: string, faults: Api.EscalatorFault[]): void => {
    const { updateEscalatorFaultBindings } = this.props;
    if (!updateEscalatorFaultBindings) return;
    const faultIdList = faults.map(fault => fault.id) as number[];
    const bindingsDetails: Api.EscalatorFaultBulkBindings = {
      targetVersionId: this.state.escalatorVersionId,
      faultIdList,
      domainId: this.state.domainId
    };
    updateEscalatorFaultBindings(operation, bindingsDetails);
  };
}

export default ModifyEscalatorParameterBindings;